import React, { useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Alert from '@mui/material/Alert';

import { TlAlertDispatch } from '../form-elements/tl-alert-dispatch';
import { receiveUser } from '../../redux/actions/user-actions';
import { updateUser } from '../../services/user-service';

import colorVariables from '../../styles/variables';
import { textField } from '../../styles/styles';

/** @type {Object<string, import('@mui/styles').CSSProperties>} */
const styles = {
  textField,
  passwordField: {
    borderRadius: 4,
    '& .MuiFilledInput-marginDense': {
      backgroundColor: colorVariables.red50,
      '&:hover': {
        backgroundColor: colorVariables.red100,
      },
      '&:focused': {
        backgroundColor: colorVariables.red100,
      },
    },
  },
  twoCol: {
    display: 'flex',
    justifyContent: 'space-between',
    '& div:first-child': {
      padding: '0 20px 0 0',
    },
  },
};

const useStyles = makeStyles(styles);

const UserDialog = ({ dialogOpen, handleDialogClose }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const alertDispatch = useContext(TlAlertDispatch);

  const [errors, setErrors] = useState({});

  const user = useSelector((/** @type {Object} */ state) => state.user);
  const [updatedUser, setUpdatedUser] = useState(null);

  const handleAction = () => {
    updateUser(updatedUser)
      .then((u) => {
        dispatch(receiveUser(u));
        alertDispatch({ status: 'success', message: 'Successfully Saved!' });
        handleDialogClose();
      })
      .catch(({ response: { data: err } }) => {
        setErrors(err);
      });
  };

  const handleFieldChange = (e) => {
    const {
      target: { id: field, value },
    } = e;
    setUpdatedUser({ ...user, [field]: value });
  };

  const { email, first_name, last_name } = updatedUser || user;

  return (
    <Dialog open={dialogOpen} onClose={handleDialogClose} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">Edit User</DialogTitle>
      {errors['/'] && <Alert severity="error">{errors['/']}</Alert>}
      <DialogContent>
        <TextField
          autoFocus
          className={classes.textField}
          error={Boolean(errors['/email'])}
          fullWidth
          helperText={errors['/email']}
          id="email"
          label="Email Address"
          margin="dense"
          onChange={(e) => handleFieldChange(e)}
          type="email"
          value={email || ''}
          variant="standard"
        />

        <Box className={classes.twoCol}>
          <TextField
            className={classes.textField}
            error={Boolean(errors['/first_name'])}
            helperText={errors['/first_name']}
            id="first_name"
            label="First Name"
            margin="dense"
            onChange={(e) => handleFieldChange(e)}
            value={first_name || ''}
            variant="standard"
          />
          <TextField
            className={classes.textField}
            id="last_name"
            label="Last Name"
            margin="dense"
            onChange={(e) => handleFieldChange(e)}
            value={last_name || ''}
            variant="standard"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={(event) => handleDialogClose(event, 'cancel')} color="primary">
          Cancel
        </Button>
        <Button onClick={handleAction} color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

UserDialog.propTypes = {
  dialogOpen: PropTypes.bool.isRequired,
  handleDialogClose: PropTypes.func.isRequired,
};

export default UserDialog;
